/**
 * SearchTrendingListReq 类型和 Schema 定义
 *
 * ⚠️ 注意: 此文件由脚本自动生成，请勿手动修改
 * 如需更新，请运行代码生成脚本
 */
import type { SchemaDefinition } from './_schema'

/**
 * SearchTrendingListReq
 */
export interface SearchTrendingListReq {
  /** 搜索关键词 */
  q?: string
  /** 是否删除 */
  deleted?: boolean
  /** 页码 */
  page?: number
  /** 每页数量 */
  page_size?: number
  /** 排序字段 */
  order_by?: string
  /** 排序方向 */
  order?: string
  /** 创建时间范围 (start,end) */
  created_at__range?: any
  /** 更新时间范围 (start,end) */
  updated_at__range?: any
  /** 更新时间距今 (7d/1h/30m) */
  updated_at__since?: any
  /** 禁用状态 */
  disabled?: boolean
  /** ID列表 */
  id__in?: string[]
  /** 统计周期类型 */
  period_type?: string
  /** 是否新上榜 */
  is_new?: boolean
  /** 是否热搜 */
  is_hot?: boolean
  /** 是否隐藏 */
  is_hidden?: boolean
  /** 关键词包含 */
  keyword__like?: string
  /** 最小搜索次数 */
  search_count__gte?: number
  /** 最大搜索次数 */
  search_count__lte?: number
  /** 最小唯一用户数 */
  unique_users__gte?: number
  /** 最大唯一用户数 */
  unique_users__lte?: number
  /** 最小排名 */
  rank__gte?: number
  /** 最大排名 */
  rank__lte?: number
  /** 最小热度分数 */
  heat_score__gte?: number
  /** 最大热度分数 */
  heat_score__lte?: number
  /** 最小运营权重 */
  admin_boost__gte?: number
  /** 最大运营权重 */
  admin_boost__lte?: number
  /** 周期开始时间距今(如:7d,1h,30m) */
  period_start__since?: any
  /** 周期开始时间早于(如:7d,1h,30m) */
  period_start__before?: any
  /** 统计周期类型列表 */
  period_type__in?: string[]
}

/**
 * SearchTrendingListReq Schema
 */
export const SearchTrendingListReqSchema: SchemaDefinition = {
  properties: {
    q: {
      description: '搜索关键词',
      format: 'string',
      type: 'string',
      label: '搜索关键词',
    },
    deleted: {
      description: '是否删除',
      format: '*bool',
      type: 'boolean',
      label: '是否删除',
    },
    page: {
      description: '页码',
      format: 'int',
      type: 'integer',
      label: '页码',
    },
    page_size: {
      description: '每页数量',
      format: 'int',
      type: 'integer',
      label: '每页数量',
    },
    order_by: {
      description: '排序字段',
      format: 'string',
      type: 'string',
      label: '排序字段',
    },
    order: {
      description: '排序方向',
      format: 'string',
      type: 'string',
      label: '排序方向',
    },
    created_at__range: {
      description: '创建时间范围 (start,end)',
      format: '[]string',
      type: 'datetime',
      label: '创建时间范围',
    },
    updated_at__range: {
      description: '更新时间范围 (start,end)',
      format: '[]string',
      type: 'datetime',
      label: '更新时间范围',
    },
    updated_at__since: {
      description: '更新时间距今 (7d/1h/30m)',
      format: '[]string',
      type: 'datetime',
      label: '更新时间距今',
    },
    disabled: {
      description: '禁用状态',
      format: '*bool',
      type: 'boolean',
      label: '禁用状态',
    },
    id__in: {
      description: 'ID列表',
      format: '[]string',
      items: {
        format: 'string',
        type: 'string',
      },
      type: 'array',
      label: 'ID列表',
    },
    period_type: {
      description: '统计周期类型',
      format: 'cons.StatsPeriodType',
      type: 'string',
      label: '统计周期类型',
    },
    is_new: {
      description: '是否新上榜',
      format: '*bool',
      type: 'boolean',
      label: '是否新上榜',
    },
    is_hot: {
      description: '是否热搜',
      format: '*bool',
      type: 'boolean',
      label: '是否热搜',
    },
    is_hidden: {
      description: '是否隐藏',
      format: '*bool',
      type: 'boolean',
      label: '是否隐藏',
    },
    keyword__like: {
      description: '关键词包含',
      format: 'string',
      type: 'string',
      label: '关键词包含',
    },
    search_count__gte: {
      description: '最小搜索次数',
      format: '*int',
      type: 'integer',
      label: '最小搜索次数',
    },
    search_count__lte: {
      description: '最大搜索次数',
      format: '*int',
      type: 'integer',
      label: '最大搜索次数',
    },
    unique_users__gte: {
      description: '最小唯一用户数',
      format: '*int',
      type: 'integer',
      label: '最小唯一用户数',
    },
    unique_users__lte: {
      description: '最大唯一用户数',
      format: '*int',
      type: 'integer',
      label: '最大唯一用户数',
    },
    rank__gte: {
      description: '最小排名',
      format: '*int',
      type: 'integer',
      label: '最小排名',
    },
    rank__lte: {
      description: '最大排名',
      format: '*int',
      type: 'integer',
      label: '最大排名',
    },
    heat_score__gte: {
      description: '最小热度分数',
      format: '*float64',
      type: 'number',
      label: '最小热度分数',
    },
    heat_score__lte: {
      description: '最大热度分数',
      format: '*float64',
      type: 'number',
      label: '最大热度分数',
    },
    admin_boost__gte: {
      description: '最小运营权重',
      format: '*float64',
      type: 'number',
      label: '最小运营权重',
    },
    admin_boost__lte: {
      description: '最大运营权重',
      format: '*float64',
      type: 'number',
      label: '最大运营权重',
    },
    period_start__since: {
      description: '周期开始时间距今(如:7d,1h,30m)',
      format: '*string',
      type: 'datetime',
      label: '周期开始时间距今',
    },
    period_start__before: {
      description: '周期开始时间早于(如:7d,1h,30m)',
      format: '*string',
      type: 'datetime',
      label: '周期开始时间早于',
    },
    period_type__in: {
      description: '统计周期类型列表',
      format: '[]cons.StatsPeriodType',
      items: {
        format: 'cons.StatsPeriodType',
        type: 'string',
      },
      type: 'array',
      label: '统计周期类型列表',
    },
  },
  type: 'object',
}
